import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { RegimeBadge, Loader, Empty, DataTag } from "@/components/widgets";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { AssetLogo } from "@/components/AssetLogo";

export default function Scanner() {
  const nav = useNavigate();
  const [sectors, setSectors] = useState([]);
  const [filters, setFilters] = useState({ min_score: 0, direction: "any", sector: "any", max_risk: "any" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/scanner/universe").then((r) => setSectors(r.data.sectors)); run(); }, []);

  const run = async () => {
    setLoading(true);
    const body = {
      min_score: filters.min_score, limit: 40,
      direction: filters.direction === "any" ? null : filters.direction,
      sector: filters.sector === "any" ? null : filters.sector,
      max_risk: filters.max_risk === "any" ? null : filters.max_risk,
    };
    try { const r = await api.post("/scanner", body); setData(r.data); } finally { setLoading(false); }
  };

  const dirCls = (d) => d === "bullish" ? "pos" : d === "bearish" ? "neg" : "text-muted-foreground";
  const riskCls = (r) => r === "low" ? "text-signal-green" : r === "medium" ? "text-signal-yellow" : "text-signal-red";

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <div className="label-xs">Strategy Layer</div>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tighter">Market Scanner</h1>
        </div>
        <div className="flex items-center gap-3">
          <DataTag kind="AI" />
          {data && <RegimeBadge regime={data.regime} />}
        </div>
      </div>

      {/* Filters */}
      <div className="mt-4 border border-border bg-card p-5 grid grid-cols-1 md:grid-cols-4 gap-5">
        <div>
          <div className="label-xs mb-3">Min AI Score · <span className="mono text-white">{filters.min_score}</span></div>
          <Slider data-testid="score-slider" value={[filters.min_score]} max={100} step={5} onValueChange={(v) => setFilters({ ...filters, min_score: v[0] })} />
        </div>
        <FilterSelect label="Direction" testid="direction-filter" value={filters.direction} onChange={(v) => setFilters({ ...filters, direction: v })}
          options={[["any", "Any"], ["bullish", "Bullish"], ["neutral", "Neutral"], ["bearish", "Bearish"]]} />
        <FilterSelect label="Max Risk" testid="risk-filter" value={filters.max_risk} onChange={(v) => setFilters({ ...filters, max_risk: v })}
          options={[["any", "Any"], ["low", "Low"], ["medium", "Medium"], ["high", "High"]]} />
        <FilterSelect label="Sector" testid="sector-filter" value={filters.sector} onChange={(v) => setFilters({ ...filters, sector: v })}
          options={[["any", "All sectors"], ...sectors.map((s) => [s, s])]} />
      </div>
      <Button data-testid="run-scan-btn" onClick={run} disabled={loading} className="mt-3 bg-volt hover:bg-volt/90 rounded-sm gap-2">
        <MagnifyingGlass size={16} /> {loading ? "Scanning…" : "Run Scan"}
      </Button>

      {loading && <Loader label="Scoring universe" />}
      {!loading && data && (
        <div className="mt-6 border border-border bg-card overflow-hidden">
          <div className="grid grid-cols-[1fr_90px_110px_90px_90px_2fr] gap-2 border-b border-border px-4 py-2.5 label-xs">
            <span>Symbol</span><span className="text-right">Score</span><span className="text-center">Direction</span>
            <span className="text-right">RSI</span><span className="text-right">Risk</span><span>Top Factor</span>
          </div>
          {data.results.length === 0 ? <Empty>No opportunities match these filters</Empty> :
            data.results.map((r) => (
              <button key={r.ticker} data-testid={`scan-row-${r.ticker}`} onClick={() => nav(`/stock/${r.ticker}`)}
                className="grid w-full grid-cols-[1fr_90px_110px_90px_90px_2fr] gap-2 items-center px-4 py-3 border-b border-border/50 hover:bg-white/[0.03] transition-colors duration-200 text-left">
                <div className="flex items-center gap-2.5"><AssetLogo ticker={r.ticker} size={26} /><div><div className="mono font-medium">{r.ticker}</div><div className="text-xs text-muted-foreground truncate max-w-[160px]">{r.name}</div></div></div>
                <div className="mono text-right text-lg font-semibold ai-glow" style={{ color: r.direction === "bullish" ? "#34C759" : r.direction === "bearish" ? "#FF3B30" : "#007AFF" }}>{r.score}</div>
                <div className={`text-center mono text-xs uppercase ${dirCls(r.direction)}`}>{r.direction}</div>
                <div className="mono text-right">{r.rsi}</div>
                <div className={`mono text-right text-xs uppercase ${riskCls(r.risk_level)}`}>{r.risk_level}</div>
                <div className="text-xs text-muted-foreground truncate">{r.top_factor}</div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <div className="label-xs mb-2">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="bg-background rounded-sm h-9"><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
